import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { IoClose } from 'react-icons/io5';

import {
  fetchItemShares,
  fetchProjectMembers,
  shareItem,
  transferChat,
  unshareItem,
} from '../../services/api';
import { taskTitle } from '../../utils/taskTitle';

// The project token carries the project and the signed-in user.
export const readProjectToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return { projectId: payload.project_id, userId: payload.user_id };
  } catch (error) {
    return {};
  }
};

// Share one chat with a project member, or hand it over to them. People on
// another team see only this chat, nothing else of the owner's team.
const KIND = {
  chat: 'chat',
  commitment: 'commitment',
  concern: 'risk or dependency',
  deliverable: 'deliverable',
  dashboard: 'dashboard',
};

// `task` is a chat; `item` ({ type, id, title }) is any other shareable item.
const ShareChatModal = ({
  task,
  item,
  accessToken,
  onClose,
  onTransferred,
}) => {
  const itemType = item?.type || 'chat';
  const itemId = item?.id || task?._id;
  const itemTitle = item?.title || taskTitle(task);
  const kind = KIND[itemType] || 'item';
  const { projectId, userId } = readProjectToken(accessToken || '');
  const [members, setMembers] = useState([]);
  const [shared, setShared] = useState(new Set());
  const [state, setState] = useState('loading'); // loading | ready | denied
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [membersRes, sharesRes] = await Promise.all([
        fetchProjectMembers(accessToken, projectId),
        fetchItemShares(accessToken, projectId, itemType, itemId),
      ]);
      setMembers((membersRes.data || []).filter((m) => m.user_id !== userId));
      setShared(new Set((sharesRes.data || []).map((row) => row.user_id)));
      setState('ready');
    } catch (error) {
      setState('denied');
    }
  }, [accessToken, projectId, itemType, itemId, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const name = (member) => member.name || member.email;

  const toggle = async (member) => {
    setBusy(member.user_id);
    const isShared = shared.has(member.user_id);
    try {
      await (isShared ? unshareItem : shareItem)(
        accessToken,
        projectId,
        itemType,
        itemId,
        member.user_id,
      );
      setShared((current) => {
        const next = new Set(current);
        if (isShared) next.delete(member.user_id);
        else next.add(member.user_id);
        return next;
      });
      toast.success(
        isShared
          ? `${name(member)} no longer has access`
          : `${name(member)} can now read this ${kind}`,
      );
    } catch (error) {
      toast.error(String(error));
    } finally {
      setBusy(null);
    }
  };

  const transfer = async (member) => {
    setBusy(member.user_id);
    try {
      await transferChat(accessToken, projectId, itemId, member.user_id);
      toast.success(`${name(member)} now owns this chat`);
      onTransferred?.(member);
      onClose();
    } catch (error) {
      toast.error(String(error));
      setBusy(null);
    }
  };

  // On the page body: the sidebar this opens from would otherwise confine it.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-label={`Share ${kind}`}
        className="w-full max-w-md rounded-xl border border-zinc-700 bg-zinc-900 p-4 text-zinc-200 shadow-xl"
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Share this {kind}</h2>
          <button onClick={onClose} title="Close" className="p-1 text-zinc-400">
            <IoClose />
          </button>
        </div>
        <p className="truncate text-xs text-zinc-400">{itemTitle}</p>
        <p className="mb-3 mt-1 text-[11px] leading-snug text-zinc-500">
          <strong>Give access</strong> lets that person read this {kind}. They
          cannot change it, and they see nothing else of your team.{' '}
          <strong>Remove access</strong> takes it away again.
          {itemType === 'chat' &&
            ' Transfer makes that person the owner, so they can continue the work; you can still read it.'}
        </p>

        {state === 'loading' && (
          <p className="text-xs text-zinc-500">Loading…</p>
        )}
        {state === 'denied' && (
          <p className="text-xs text-amber-300">
            You cannot share this {kind}. Only its owner, their team lead or a
            project manager can.
          </p>
        )}
        {state === 'ready' && members.length === 0 && (
          <p className="text-xs text-zinc-500">
            There is nobody else in this project yet.
          </p>
        )}
        {state === 'ready' && members.length > 0 && (
          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {members.map((member) => (
              <li
                key={member.user_id}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-zinc-800"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs">{name(member)}</p>
                  <p className="truncate text-[11px] text-zinc-500">
                    {member.team ? `Team ${member.team}` : 'No team'}
                  </p>
                </div>
                <button
                  onClick={() => toggle(member)}
                  disabled={busy === member.user_id}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium disabled:opacity-40 ${
                    shared.has(member.user_id)
                      ? 'bg-zinc-700 text-zinc-200'
                      : 'bg-[#5652CF] text-white'
                  }`}
                >
                  {shared.has(member.user_id) ? 'Remove access' : 'Give access'}
                </button>
                {itemType === 'chat' && (
                  <button
                    onClick={() => transfer(member)}
                    disabled={busy === member.user_id}
                    title="Make this person the owner. You can still read it."
                    className="rounded-md border border-zinc-600 px-2 py-1 text-[11px] text-zinc-300 disabled:opacity-40"
                  >
                    Transfer
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ShareChatModal;
